import {
    Card,
    Heading,
    SelectFilled,
    MultilineInput,
    Toggle,
    Text,
    CheckboxGroup,
    ButtonGradientPrimary,
    ButtonGhost,
    Spinner,
    Colors,
    Icon,
    TextInput,
    Modal,
    CIRCLE_DIMENSIONS,
    COMPONENT_RADIUS,
    FOCUS_RING_NO_RADIUS,
    defaultHover,
    useDialogFocus,
    ButtonSecondary,
    ButtonCancel,
} from '@amelio/ui-library';
import { useAtomValue } from 'jotai';
import { useId, useMemo, useState } from 'react';

import { Question } from '../Question/Question';

import type { TranslationKey } from '@/i18n-key';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { userInstanceAtom } from '@/atoms/userDetails';
import { FormField } from '@/components/FormField/FormField';
import { AdvancedOptionType } from '@/constants/AdvancedOptionType';
import {
    EvaluationType,
    type EvaluationTypeValue,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import { QuestionType, type QuestionTypeValue } from '@/constants/QuestionType';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { CompanyValueAssignment } from '@/pages/ManageTemplates/TemplateQuestionnaire/components/CompanyValueAssignment';
import { getTextByLanguage } from '@/utils/localization';
import {
    createEmptyQuestion,
    filterOutOtherOption,
    getAdvancedOptions,
    getQuestionTypeOptions,
} from '@/utils/question';

const validateQuestionForSave = ({
    question,
    language,
}: {
    question: ReviewQuestion;
    language: string;
}): TranslationKey | null => {
    const sortedOptions = [...(question.reviewQuestionOptions ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder,
    );

    if (question.questionType === QuestionType.RATING_SCALE) {
        const fromOption = sortedOptions[0];
        const toOption = sortedOptions[1];
        const fromValue = fromOption?.scoreValue;
        const toValue = toOption?.scoreValue;

        if (
            fromValue == null ||
            toValue == null ||
            !Number.isInteger(fromValue) ||
            !Number.isInteger(toValue) ||
            fromValue < 0
        ) {
            return 'evaluation360Questionnaire.validation.ratingScaleBoundsInvalid';
        }

        if (fromValue >= toValue) {
            return 'evaluation360Questionnaire.validation.ratingScaleOrder';
        }

        if (
            !getTextByLanguage(fromOption?.label, language).trim() ||
            !getTextByLanguage(toOption?.label, language).trim()
        ) {
            return 'evaluation360Questionnaire.validation.scaleLabelRequired';
        }
    }

    if (
        question.questionType === QuestionType.MULTI_CHOICE ||
        question.questionType === QuestionType.RANKING
    ) {
        const options =
            question.questionType === QuestionType.MULTI_CHOICE
                ? filterOutOtherOption(question.reviewQuestionOptions)
                : (question.reviewQuestionOptions ?? []);
        const labels = options.map((option) => getTextByLanguage(option.label, language).trim());
        const minOptionsErrorKey =
            question.questionType === QuestionType.MULTI_CHOICE
                ? 'evaluation360Questionnaire.validation.multipleChoiceMinOptions'
                : 'evaluation360Questionnaire.validation.rankingMinOptions';

        if (labels.length < 2) {
            return minOptionsErrorKey;
        }

        if (question.questionType === QuestionType.MULTI_CHOICE) {
            if (labels.some((label) => !label)) {
                return 'evaluation360Questionnaire.validation.multipleChoiceOptionEmpty';
            }

            const seen = new Set<string>();
            for (const label of labels) {
                const normalized = label.toLowerCase();
                if (seen.has(normalized)) {
                    return 'evaluation360Questionnaire.validation.multipleChoiceOptionDuplicate';
                }
                seen.add(normalized);
            }
        }
    }

    if (question.questionType === QuestionType.EMOJI_CHOICE) {
        const hasEmptyLabel = sortedOptions.some(
            (option) => !getTextByLanguage(option.label, language).trim(),
        );
        if (hasEmptyLabel) {
            return 'evaluation360Questionnaire.validation.satisfactionLabelRequired';
        }
    }

    return null;
};

export type QuestionDefaults = {
    satisfactionLabels?: string[];
    scaleFromLabel?: string;
    scaleToLabel?: string;
    hideOtherOption?: boolean;
    emptyOptionCount?: number;
};

interface QuestionFormProps {
    mode: 'add' | 'edit';
    question?: ReviewQuestion;
    onQuestionChange: (question: ReviewQuestion) => void;
    onCancel: () => void;
    onSave: (question: ReviewQuestion) => void;
    isPendingCreateQuestion?: boolean;
    isPendingUpdateQuestion?: boolean;
    allowedTypes?: QuestionTypeValue[];
    allowQuestionTypeEdit?: boolean;
    hideAdvancedOptions?: boolean;
    requireLabel?: boolean;
    questionDefaults?: QuestionDefaults;
    showQuestionHelp?: boolean;
    validateOnSave?: boolean;
    reviewType?: EvaluationTypeValue;
    readOnly?: boolean;
}

export const QuestionForm = ({
    mode,
    question,
    onQuestionChange,
    onCancel,
    onSave,
    isPendingCreateQuestion,
    isPendingUpdateQuestion,
    allowedTypes,
    allowQuestionTypeEdit = false,
    hideAdvancedOptions,
    requireLabel,
    questionDefaults,
    showQuestionHelp,
    validateOnSave,
    reviewType,
    readOnly = false,
}: QuestionFormProps) => {
    const { t } = useAmelioTranslations();
    const defaultLanguage = useSelectedAppLanguage();
    const currentUser = useAtomValue(userInstanceAtom);

    const emptyQuestion = createEmptyQuestion(defaultLanguage);

    const [labelError, setLabelError] = useState(false);

    const [validationErrorKey, setValidationErrorKey] = useState<TranslationKey | null>(null);

    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const helpTitleId = useId();
    const { dialogRef: helpDialogRef, handleKeyDown: handleHelpKeyDown } =
        useDialogFocus(isHelpOpen);
    const closeHelp = () => setIsHelpOpen(false);

    const isLoading = isPendingCreateQuestion || isPendingUpdateQuestion;

    const isCompanyValueQuestion = !!question?.companyValueId;

    const questionTypeOptions = useMemo(() => {
        const allOptions = getQuestionTypeOptions(t);

        if (!allowedTypes) {
            return allOptions;
        }

        return allOptions.filter((option) =>
            allowedTypes.includes(option.value as QuestionTypeValue),
        );
    }, [t, allowedTypes]);

    const additionalOptionValues = useMemo(() => {
        if (!question) {
            return [];
        }

        const additionalOptionValues: string[] = [];

        if (question.isAnswerMandatory) {
            additionalOptionValues.push(AdvancedOptionType.DO_NOT_ALLOW_SKIP);
        }

        return additionalOptionValues;
    }, [question]);

    const handleAdvancedOptionsChange = (value: string[]) => {
        onQuestionChange({
            ...(question || emptyQuestion),
            isAnswerMandatory: value.includes(AdvancedOptionType.DO_NOT_ALLOW_SKIP),
        });
    };

    const handleToggleChange = (value: boolean) => {
        onQuestionChange({
            ...(question || emptyQuestion),
            isCommentAllowed:
                question?.questionType === QuestionType.PLAIN_TEXT ||
                question?.questionType === QuestionType.SECTION_TITLE
                    ? false
                    : value,
        });
    };

    const buttonTitle = mode === 'edit' ? t('common.update') : t('common.add');

    return (
        <Card
            className={'z-2 w-full border'}
            borderRadius={'2xl'}
            padding={'lg'}
            bgColor={'bg-white'}
            borderColor={Colors.BASE_100}
        >
            {readOnly && (
                <div
                    className={
                        'mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-700'
                    }
                >
                    <Icon
                        name={'security-lock'}
                        size={'sm'}
                    />
                    <Text size={'small'}>{t('visionValues.questionFrozenInfo')}</Text>
                </div>
            )}
            <div className={readOnly ? 'pointer-events-none opacity-60' : ''}>
                <div className={'flex flex-col justify-between md:flex-row'}>
                    <Heading
                        level={3}
                        weight={'medium'}
                    >
                        {mode === 'edit'
                            ? t('evaluation360Questionnaire.questionForm.titleEdit')
                            : t('evaluation360Questionnaire.questionForm.title')}
                    </Heading>

                    <div className={'flex flex-row items-center gap-4'}>
                        {!hideAdvancedOptions &&
                            question?.questionType !== QuestionType.PLAIN_TEXT &&
                            question?.questionType !== QuestionType.SECTION_TITLE && (
                                <Toggle
                                    leftLabel={t(
                                        'evaluation360Questionnaire.questionForm.allowComments',
                                    )}
                                    onChange={handleToggleChange}
                                    checked={question?.isCommentAllowed}
                                />
                            )}

                        {showQuestionHelp && (
                            <ButtonGhost
                                size={'sm'}
                                onClick={() => setIsHelpOpen(true)}
                                ariaLabel={t('competencies.questionHelp.button')}
                                dataTestId={'question-help-button'}
                                leftIcon={
                                    <Icon
                                        name={'support-message-question'}
                                        size={'sm'}
                                        color={Colors.PRIMARY_700}
                                    />
                                }
                            >
                                {t('competencies.questionHelp.button')}
                            </ButtonGhost>
                        )}
                    </div>
                </div>

                <div className={'qf-row mt-8'}>
                    <FormField
                        label={t('common.question')}
                        className={`flex-2 ${labelError ? 'amelio-blink' : ''}`}
                        errorMessage={
                            labelError
                                ? t('evaluation360Questionnaire.validation.questionLabelRequired')
                                : undefined
                        }
                    >
                        <MultilineInput
                            value={getTextByLanguage(question?.label, defaultLanguage)}
                            onChange={(value: string) => {
                                if (labelError && value.trim()) {
                                    setLabelError(false);
                                }
                                onQuestionChange({
                                    ...(question || emptyQuestion),
                                    label: { ...question?.label, [defaultLanguage]: value },
                                });
                            }}
                            placeholder={t(
                                'evaluation360Questionnaire.questionForm.questions.placeholder',
                            )}
                            autoGrow={true}
                            rows={3}
                        />
                    </FormField>

                    <FormField
                        label={t('common.questionType')}
                        className={'flex-1'}
                        disabled={mode === 'edit' && !allowQuestionTypeEdit}
                    >
                        <SelectFilled
                            value={question?.questionType}
                            options={questionTypeOptions}
                            onChange={(value: string) => {
                                const currentQuestion = question || emptyQuestion;
                                const questionType = value as QuestionTypeValue;

                                if (currentQuestion.questionType === questionType) {
                                    onQuestionChange(currentQuestion);

                                    return;
                                }

                                const isCommentAllowedForType =
                                    questionType === QuestionType.PLAIN_TEXT ||
                                    questionType === QuestionType.SECTION_TITLE
                                        ? false
                                        : true;
                                onQuestionChange({
                                    ...currentQuestion,
                                    questionType,
                                    isCommentAllowed: isCommentAllowedForType,
                                    reviewQuestionOptions: [],
                                });
                            }}
                            placeholder={t(
                                'evaluation360Questionnaire.questionForm.selectQuestionPlaceholder',
                            )}
                            disabled={mode === 'edit' && !allowQuestionTypeEdit}
                            hideSelectedOption={true}
                        />
                    </FormField>
                </div>

                <Question
                    question={question || emptyQuestion}
                    questionDefaults={{
                        ...questionDefaults,
                        hideOtherOption:
                            questionDefaults?.hideOtherOption || isCompanyValueQuestion,
                    }}
                    setQuestion={(updatedQuestion) => {
                        if (validationErrorKey) {
                            setValidationErrorKey(null);
                        }
                        onQuestionChange(updatedQuestion);
                    }}
                />

                {/* Company Value Assignment - Only for Manager Evaluation and Company Admins (not Super Admins) */}
                {reviewType === EvaluationType.MANAGER_EVALUATION &&
                    !currentUser?.isAmelioAdmin && (
                        <CompanyValueAssignment
                            question={question}
                            onQuestionChange={onQuestionChange}
                        />
                    )}

                {validationErrorKey && (
                    <div
                        className={'mt-3 flex items-center gap-1'}
                        role={'alert'}
                        aria-live={'polite'}
                        data-testid={'question-validation-error'}
                    >
                        <Icon
                            name={'essential-danger'}
                            size={'sm'}
                            color={Colors.ERROR}
                        />

                        <Text
                            size={'small'}
                            color={Colors.ERROR}
                        >
                            {t(validationErrorKey)}
                        </Text>
                    </div>
                )}

                {!hideAdvancedOptions &&
                    getAdvancedOptions({ questionType: question?.questionType, t }).length > 0 && (
                        <>
                            <Text
                                className={'mt-8'}
                                weight={'medium'}
                            >
                                {t('common.advancedOptions')}
                            </Text>

                            {question?.questionType === QuestionType.CHECKBOX && (
                                <div className={'my-4 max-w-65'}>
                                    <TextInput
                                        type={'number'}
                                        dataTestId={'maximum-choices-input'}
                                        value={
                                            question?.maximumChoices != null
                                                ? String(question.maximumChoices)
                                                : ''
                                        }
                                        onChange={(value) => {
                                            const parsed = parseInt(value, 10);
                                            onQuestionChange({
                                                ...(question || emptyQuestion),
                                                maximumChoices: isNaN(parsed) ? null : parsed,
                                            });
                                        }}
                                        ariaLabel={t(
                                            'evaluation360Questionnaire.questionForm.questions.checkbox.maximumChoicesLabel',
                                        )}
                                        placeholder={t(
                                            'evaluation360Questionnaire.questionForm.questions.checkbox.maximumChoicesLabel',
                                        )}
                                    />
                                </div>
                            )}

                            <CheckboxGroup
                                className={'mt-2 opacity-60'}
                                options={getAdvancedOptions({
                                    questionType: question?.questionType,
                                    t,
                                })}
                                name={'advanced-options'}
                                onChange={handleAdvancedOptionsChange}
                                values={additionalOptionValues}
                            />
                        </>
                    )}
            </div>
            {readOnly ? (
                <div className={'mt-8 flex w-full justify-end'}>
                    <ButtonSecondary
                        className={'min-w-30'}
                        onClick={onCancel}
                    >
                        {t('common.close')}
                    </ButtonSecondary>
                </div>
            ) : (
                <div className={'mt-8 flex w-full justify-start gap-4'}>
                    <ButtonCancel
                        size={'md'}
                        fitContent
                        onClick={onCancel}
                    >
                        {t('common.cancel')}
                    </ButtonCancel>
                    <ButtonGradientPrimary
                        size={'md'}
                        className={'!min-w-30'}
                        onClick={() => {
                            const questionToSave = question || emptyQuestion;

                            if (
                                requireLabel &&
                                !getTextByLanguage(questionToSave.label, defaultLanguage).trim()
                            ) {
                                setLabelError(true);

                                return;
                            }

                            if (validateOnSave) {
                                const contentErrorKey = validateQuestionForSave({
                                    question: questionToSave,
                                    language: defaultLanguage,
                                });

                                if (contentErrorKey) {
                                    setValidationErrorKey(contentErrorKey);

                                    return;
                                }
                            }

                            setValidationErrorKey(null);

                            const isRestrictedType =
                                questionToSave.questionType === QuestionType.PLAIN_TEXT ||
                                questionToSave.questionType === QuestionType.SECTION_TITLE;
                            onSave({
                                ...questionToSave,
                                isCommentAllowed: isRestrictedType
                                    ? false
                                    : questionToSave.isCommentAllowed,
                                hasOtherOption: isCompanyValueQuestion
                                    ? false
                                    : questionToSave.hasOtherOption,
                            });
                        }}
                        disabled={isLoading}
                        leftIcon={
                            !isLoading && mode === 'add' ? (
                                <Icon
                                    name={'essential-add'}
                                    color={Colors.WHITE}
                                    size={'lg'}
                                />
                            ) : undefined
                        }
                    >
                        {isLoading ? (
                            <Spinner
                                color={Colors.PRIMARY_700}
                                size={'xs'}
                            />
                        ) : (
                            buttonTitle
                        )}
                    </ButtonGradientPrimary>
                </div>
            )}

            {showQuestionHelp && (
                <Modal
                    isOpen={isHelpOpen}
                    onClose={closeHelp}
                    className={'w-[640px] max-w-[92vw]'}
                    dataTestId={'question-help-modal'}
                >
                    <div
                        ref={helpDialogRef}
                        role={'dialog'}
                        aria-modal={'true'}
                        aria-labelledby={helpTitleId}
                        onKeyDown={handleHelpKeyDown}
                        className={'flex flex-col gap-5'}
                    >
                        <div className={'flex items-center justify-between gap-4'}>
                            <Heading
                                level={4}
                                weight={'medium'}
                                id={helpTitleId}
                            >
                                {t('competencies.questionHelp.title')}
                            </Heading>

                            <button
                                type={'button'}
                                onClick={closeHelp}
                                aria-label={t('common.close')}
                                data-testid={'question-help-modal-close'}
                                className={`${FOCUS_RING_NO_RADIUS} ${COMPONENT_RADIUS.full} ${defaultHover} hover:bg-base-100 flex ${CIRCLE_DIMENSIONS.md} shrink-0 items-center justify-center`}
                            >
                                <Icon
                                    name={'essential-close'}
                                    color={Colors.BASE_500}
                                    size={'sm'}
                                    ariaHidden
                                />
                            </button>
                        </div>

                        <div className={'flex flex-col gap-4'}>
                            <Text color={Colors.BASE_500}>
                                {t('competencies.questionHelp.intro')}
                            </Text>

                            <div>
                                <Text weight={'medium'}>
                                    {t('competencies.questionHelp.whyTitle')}
                                </Text>

                                <Text color={Colors.BASE_500}>
                                    {t('competencies.questionHelp.why')}
                                </Text>
                            </div>

                            <div>
                                <Text weight={'medium'}>
                                    {t('competencies.questionHelp.goalTitle')}
                                </Text>

                                <Text color={Colors.BASE_500}>
                                    {t('competencies.questionHelp.goal')}
                                </Text>
                            </div>
                        </div>

                        <div className={'flex flex-col gap-3'}>
                            <Text weight={'medium'}>
                                {t('competencies.questionHelp.exampleTitle')}
                            </Text>

                            <div
                                className={
                                    'rounded-2xl bg-linear-to-br from-[#3b1d6e] to-[#5b21b6] p-6'
                                }
                            >
                                <p className={'mb-5 text-lg font-bold text-white'}>
                                    {t('competencies.questionHelp.example.prompt')}
                                </p>

                                <div className={'flex flex-col gap-3'}>
                                    {[
                                        {
                                            label: t('competencies.questionHelp.example.option1'),
                                            selected: true,
                                        },
                                        {
                                            label: t('competencies.questionHelp.example.option2'),
                                            selected: false,
                                        },
                                        {
                                            label: t('competencies.questionHelp.example.option3'),
                                            selected: false,
                                        },
                                        {
                                            label: t('competencies.questionHelp.example.option4'),
                                            selected: false,
                                        },
                                    ].map((option) => (
                                        <div
                                            key={option.label}
                                            className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${
                                                option.selected
                                                    ? 'border-[#2dd4bf] bg-white/15 ring-2 ring-[#2dd4bf]'
                                                    : 'border-white/20 bg-white/10'
                                            }`}
                                        >
                                            <span
                                                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                                                    option.selected
                                                        ? 'border-[#2dd4bf]'
                                                        : 'border-white/50'
                                                }`}
                                            >
                                                {option.selected && (
                                                    <span
                                                        className={
                                                            'h-2.5 w-2.5 rounded-full bg-[#2dd4bf]'
                                                        }
                                                    />
                                                )}
                                            </span>

                                            <span className={'text-sm text-white'}>
                                                {option.label}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}
        </Card>
    );
};
